import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} DummyStudentAuth
 * @property {string} type
 * @property {boolean} tenant
 * @property {string} tenant_type
 */

/**
 * @typedef {object} DummyStudentUser
 * @property {number} id
 * @property {number} school_id
 * @property {string} tenant_id
 * @property {string} name
 * @property {string | null} email
 * @property {string} username
 * @property {number} role
 * @property {number} role_id
 * @property {string} role_slug
 * @property {string} locale
 * @property {boolean} can_change_locale
 * @property {boolean} is_dummy_student 
 */

/**
 * @typedef {object} DummyStudentToken
 * @property {string} access_token
 * @property {string} refresh_token
 * @property {number} access_token_expiry
 * @property {number} refresh_token_expiry
 */

/**
 * @typedef {object} DummyStudentSwitchData
 * @property {DummyStudentAuth} auth
 * @property {DummyStudentUser} user
 * @property {DummyStudentToken} token
 */

/**
 * @description Custom hook to switch to the dummy student account
 */
export const useSwitchDummyStudent = () => {
  const [switchData, setSwitchData] = useState(
    /** @type {DummyStudentSwitchData | null} */(null)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { axiosInstance } = useAxios();

  /**
   * Switch to the dummy student account
   * @async
   * @param {function(DummyStudentSwitchData): void} [onSuccess] - Callback on success
   */
  const switchDummyStudent = async (onSuccess) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);

      const url = `${TENANT_DOMAIN}/students/dummy/switch`;

      const response = await axiosInstance.post(url);

      const data = response?.data?.data || null;
      setSwitchData(data);
      setSuccess(true);

      if (onSuccess && data) {
        onSuccess(data);
      }
    } catch (err) {
      setSwitchData(null);
      setSuccess(false);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    switchData,
    loading,
    error,
    success,
    switchDummyStudent,
  };
};
